import './notifications.scss';

import React, { useState, useMemo } from 'react';
import DashboardLayout from '../../components/dashboard/dashboardLayout';
import InputSearch from '../../components/inputs/Search';
import Loader from '../../components/loader';
import NotificationsList from './notificationList';
import Button from '../../components/button';
import NobleIcon from '../../components/nobleIcon';
import { useNotificationsQueries } from '../../hooks/queries/use-notification.queries';
import { NOTIFICATION_ACTION_TYPES } from 'helpers/constants';
import NotificationModal from 'components/modals/notificationModal';
import AudienceDropdown from './notificationList/audienceDropdown';
import {
	AUDIENCE_TYPES,
	getNotificationsListPollingInterval,
} from 'helpers/notificationDelivery';

const LIMIT = 15;

const Notifications = () => {
	const [notification, setNotification] = useState([]);
	const [actionType, setActionType] = useState(NOTIFICATION_ACTION_TYPES.NONE);
	const [isOpen, setIsOpen] = useState(false);
	const [openDropdown, setOpenDropdown] = useState(null);
	const [currentAudience, setCurrentAudience] = useState([]);
	const [search, setSearch] = useState('');

	// The modal covers the list: no need to refresh it while it is open
	const { data, isLoading, error, refetch } = useNotificationsQueries(undefined, {
		pollingEnabled: !isOpen,
	});

	const notifications = useMemo(() => {
		if (!data?.pages || data.pages.length === 0) return [];
		const firstPage = data.pages[0];
		if (firstPage?.error) return [];
		return Array.isArray(firstPage) ? firstPage : [];
	}, [data]);

	const toggleDropdown = () => {
		setOpenDropdown(!openDropdown);
	};

	const handleSearch = (e) => {
		setSearch(e.target.value);
	};

	const openAddNotification = () => {
		setActionType(NOTIFICATION_ACTION_TYPES.ADD_NOTIFICATION);
		setIsOpen(true);
	};

	const openNotificationInfo = (selectedNotification) => {
		setActionType(NOTIFICATION_ACTION_TYPES.NOTIFICATION_INFO);
		setNotification(selectedNotification);
		setIsOpen(true);
	};

	const closeHandler = () => {
		setIsOpen(false);
		// Polling was paused while the modal was open: catch up right away
		// instead of waiting for the next interval
		if (getNotificationsListPollingInterval(notifications)) refetch();
	};

	const filteredNotifications = notifications.filter((item) => {
		// Filter by search text
		const titleMatch = item.title
			?.toLowerCase()
			.includes(search.toLowerCase());
		const messageMatch = item.message
			?.toLowerCase()
			.includes(search.toLowerCase());
		const dateMatch = item.created_at
			?.toLowerCase()
			.includes(search.toLowerCase());

		const searchMatch = titleMatch || messageMatch || dateMatch;

		// Filter by audience. A notification sent to all active members reached
		// every venue, so it matches any venue selection.
		let audienceMatch = true;
		if (
			currentAudience &&
			currentAudience.length > 0 &&
			item.audience &&
			item.audience_type !== AUDIENCE_TYPES.ALL
		) {
			// Extract audience IDs from notification (handle both old string format and new object format)
			const notificationAudienceIds = item.audience
				.map((aud) => (typeof aud === 'string' ? aud : aud.id))
				.filter((id) => id != null);
			
			// Check if any selected audience ID matches any notification audience ID
			audienceMatch = notificationAudienceIds.some((audId) =>
				currentAudience.includes(audId)
			);
		}

		return searchMatch && audienceMatch;
	});

	return (
		<DashboardLayout
			layoutClass="Notifications"
			layoutTitle="Notifications"
			contentSearch={
				<div className="Notifications__menu__btn_add_notification">
					<Button
						displayLabel={
							<div className="contain_add_notification">
								<NobleIcon name="addNotification" />
								<span>New Notification</span>
							</div>
						}
						size="full"
						// buttonValidation={!isOpen}
						onClick={() => openAddNotification()}
					/>
				</div>
			}>
			<div className="Notifications__menu">
				<div className="Notifications__menu__filter">
					<AudienceDropdown
						isOpen={openDropdown}
						onToggle={toggleDropdown}
						currentAudience={currentAudience}
						setCurrentAudience={setCurrentAudience}
					/>
				</div>
				<div className="Notifications__menu__search">
					<InputSearch
						id="search"
						type="search"
						name="search"
						placeholder="Search"
						value={search}
						onChange={(e) => handleSearch(e)}
					/>
				</div>
			</div>
			<NotificationsList
				notifications={filteredNotifications}
				openNotificationInfo={openNotificationInfo}
			/>
			{/* Only on the first load: the list refreshes itself while a send is in
			progress and the full-page loader would cover it every few seconds */}
			{isLoading && <Loader />}
			{isOpen && (
				<NotificationModal
					notification={notification}
					setNotification={setNotification}
					actionType={actionType}
					setActionType={setActionType}
					isOpen={isOpen}
					closeHandler={closeHandler}
				/>
			)}
		</DashboardLayout>
	);
};

export default Notifications;
